/**
 * Pre-pairing screen. Shows the pairing code + CLI instructions and
 * polls the Worker every 3 seconds to detect when ERPClaw finishes
 * pairing so we can transition to the Dashboard.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Layout,
  Link,
  List,
  Page,
  Spinner,
  Text,
} from "@shopify/polaris";
import { ApiError, getStatus } from "../lib/api";
import { PAIRING_CODE_TTL_SECONDS } from "../config";

type Props = {
  shop: string;
  code: string | null;
  onPaired: () => void;
};

function formatCountdown(secondsLeft: number): string {
  if (secondsLeft <= 0) return "expired";
  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function Connect({ shop, code, onPaired }: Props) {
  const [copied, setCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(PAIRING_CODE_TTL_SECONDS);
  const [pollError, setPollError] = useState<string | null>(null);

  const cliCommand = code
    ? `erpclaw shopify-connect --pairing-code ${code}`
    : "";

  const handleCopy = useCallback(async () => {
    if (!cliCommand) return;
    try {
      await navigator.clipboard.writeText(cliCommand);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard may be blocked; user can select text manually
    }
  }, [cliCommand]);

  // Countdown timer
  useEffect(() => {
    if (!code) return;
    const id = window.setInterval(() => {
      setSecondsLeft((n) => Math.max(0, n - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [code]);

  // Poll for pair completion
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const resp = await getStatus(shop);
        if (cancelled) return;
        if (resp.paired) onPaired();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setPollError(err.message);
      }
    };
    const id = window.setInterval(tick, 3000);
    tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [shop, code, onPaired]);

  if (!code) {
    return (
      <Page title="Connect ERPClaw">
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">
                    Pairing code missing or expired
                  </Text>
                  <Text as="p" variant="bodyMd">
                    Reinstall ERPClaw from the Shopify App Store to generate a fresh pairing code.
                  </Text>
                  <Link url="https://erpclaw.ai/docs/shopify" target="_blank">
                    Setup guide
                  </Link>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Connect ERPClaw">
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Step 1: Your pairing code
                </Text>
                <Box paddingBlock="200">
                  <Text as="p" variant="heading2xl" alignment="center" fontWeight="bold">
                    {code}
                  </Text>
                </Box>
                <InlineStack gap="300" align="center">
                  <Badge tone="info">
                    {`Expires in ${formatCountdown(secondsLeft)}`}
                  </Badge>
                  {secondsLeft > 0 && (
                    <InlineStack gap="200" blockAlign="center">
                      <Spinner accessibilityLabel="Waiting for ERPClaw to pair" size="small" />
                      <Text as="span" variant="bodySm" tone="subdued">
                        Waiting for ERPClaw&hellip;
                      </Text>
                    </InlineStack>
                  )}
                </InlineStack>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Step 2: Run this in your ERPClaw terminal
                </Text>
                <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                  <InlineStack align="space-between" blockAlign="center" gap="200">
                    <code style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 }}>
                      {cliCommand}
                    </code>
                    <Button onClick={handleCopy} variant="plain">
                      {copied ? "Copied!" : "Copy"}
                    </Button>
                  </InlineStack>
                </Box>
                <List type="bullet">
                  <List.Item>
                    Don&apos;t have ERPClaw yet? Install via{" "}
                    <code>clawhub install erpclaw</code> on your own machine.
                  </List.Item>
                  <List.Item>
                    This page will update automatically the moment pairing completes.
                  </List.Item>
                </List>
                <Link url="https://erpclaw.ai/docs/shopify" target="_blank">
                  Full setup guide
                </Link>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {pollError && (
          <Layout.Section>
            <Card>
              <Box padding="400">
                <Text as="p" variant="bodyMd" tone="subdued">
                  Polling error (retrying): {pollError}
                </Text>
              </Box>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
